import { ComponentType, FC } from 'react';
import type { SummaryItemSpan, SummarySectionProps } from './summary-section';

export type {
	SummaryItem,
	SummaryItemSpan,
	SummarySectionProps,
	SummarySubsection,
} from './summary-section';

export type FormStepChangeReason = 'next' | 'back' | 'jump';

export type FormStepReviewItem = {
	name?: string;
	label?: string;
	span?: SummaryItemSpan;
	format?: (values: Record<string, string>) => string;
};

export type FormStepReviewSubsection = {
	title?: string;
	items: FormStepReviewItem[];
	columns?: 1 | 2 | 3 | 4;
};

export type FormStepReview = {
	title?: string;
	subsections: FormStepReviewSubsection[];
	columns?: 1 | 2 | 3 | 4;
};

export type FormStepReviewConfig =
	| FormStepReview
	| ((values: Record<string, string>) => FormStepReview);

export type StepSlotProps = {
	currentStep: number;
	isFirstStep: boolean;
	isLastStep: boolean;
	isDirty: boolean;
	nextButtonText: string;
	onValuesChange: (patch: Record<string, string>) => void;
	onBack: () => void;
	onStepComplete: (values: Record<string, string>) => void | Promise<void>;
	initialValues: Record<string, string>;
};

export type ReviewStepSlotProps = StepSlotProps & {
	sections: SummarySectionProps[];
	goToStep: (index: number) => void;
	submitError?: string;
	isSubmitting?: boolean;
};

export type FormStep = {
	stepTitle: string;
	showStepHeader?: boolean;
	stepDescription?: string;
	stepContent: ComponentType<StepSlotProps>;
	review?: FormStepReviewConfig;
	// Callback used to fire external logic when the step is changed
	onStepChange?: (
		values: Record<string, string>,
		meta: { fromIndex: number; toIndex: number; reason: FormStepChangeReason }
	) => void | Promise<void>;
	nextButtonText?: string;
};

export type ResolvedFormStep = FormStep & { id: string };

export type StepperFormProps = {
	title: string;
	titleHTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
	description: string;
	steps: FormStep[];
	// Component used to render the review step instead of the default one inside the stepper form
	reviewContent?: ComponentType<ReviewStepSlotProps>;
	reviewNextButtonText?: string;
	// Extra review sections to render in the review step
	extraReviewSections?: SummarySectionProps[];
	extraReviewSectionsPosition?: 'start' | 'end';
	onSubmit: (formValues: Record<string, string>) => void | Promise<void>;
	successTitle: string;
	successDescription: string;
	successCtaLabel: string;
	successTitleHTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
	onSuccessCtaClick: () => void;
};

export type PendingNavigation = {
	toIndex: number;
	reason: FormStepChangeReason;
};

export type StepperFormBodyProps = {
	stepTitle: string;
	stepDescription?: string;
	stepContent: ComponentType<StepSlotProps | ReviewStepSlotProps>;
	slotProps: StepSlotProps;
	reviewProps?: Pick<ReviewStepSlotProps, 'sections' | 'goToStep' | 'submitError' | 'isSubmitting'>;
	showStepHeader?: boolean;
};

export function resolveStepId(_step: FormStep, index: number): string {
	return `step-${index}`;
}

export function toStepHash(stepTitle: string): string {
	return stepTitle
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
}

export type StepStatus = 'completed' | 'current' | 'upcoming';

export function getStepStatus(
	index: number,
	currentStep: number,
	completedSteps: ReadonlySet<number>
): StepStatus {
	if (index === currentStep) return 'current';
	if (completedSteps.has(index)) return 'completed';
	return 'upcoming';
}

export function isReviewStep(stepTitle: string): boolean {
	return stepTitle.trim().toLowerCase() === 'review';
}

export function mergeReviewSections(
	sections: SummarySectionProps[],
	extraSections: SummarySectionProps[] | undefined,
	position: 'start' | 'end' = 'end'
): SummarySectionProps[] {
	if (!extraSections?.length) return sections;
	return position === 'start' ? [...extraSections, ...sections] : [...sections, ...extraSections];
}
